import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, formatError, jobId } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const field = "rounded-none border-white/15 bg-[#061235] h-12";

const ApplyForm = ({ job }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", resume_link: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/applications", { ...f, job_id: jobId(job) });
      setDone(data);
      toast.success("Application received");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return (
    <div data-testid="apply-success" className="hairline bg-[#0B1D4F] p-8">
      <CheckCircle2 className="h-9 w-9 text-emerald-400" />
      <p className="font-display mt-4 text-3xl font-extrabold uppercase">Application received</p>
      <p className="mt-2 text-sm text-slate-300">Reference <span className="font-mono text-white">{jobId(done).slice(-8).toUpperCase()}</span>. The posting owner will follow up.</p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="apply-form" className="hairline space-y-4 bg-[#0B1D4F] p-6 md:p-8">
      <p className="font-display text-3xl font-extrabold uppercase">Apply</p>
      <div><Label htmlFor="a-name">Full name</Label><Input id="a-name" required minLength={2} value={f.name} onChange={set("name")} data-testid="apply-name-input" className={field} /></div>
      <div><Label htmlFor="a-email">Email</Label><Input id="a-email" type="email" required value={f.email} onChange={set("email")} data-testid="apply-email-input" className={field} /></div>
      <div><Label htmlFor="a-phone">Phone (optional)</Label><Input id="a-phone" value={f.phone} onChange={set("phone")} data-testid="apply-phone-input" className={field} /></div>
      <div><Label htmlFor="a-resume">Resume / LinkedIn URL</Label><Input id="a-resume" value={f.resume_link} onChange={set("resume_link")} data-testid="apply-resume-input" className={field} /></div>
      <div><Label htmlFor="a-msg">Note (optional)</Label><Textarea id="a-msg" rows={4} value={f.message} onChange={set("message")} data-testid="apply-message-input" className="rounded-none border-white/15 bg-[#061235]" /></div>
      <button type="submit" disabled={busy} data-testid="apply-submit-button" className="w-full bg-[#C8102E] px-6 py-4 font-semibold text-white transition-colors hover:bg-[#E3273F] disabled:opacity-60">{busy ? "Submitting…" : "Submit application"}</button>
    </form>
  );
};

const List = ({ title, items }) => (
  <div className="mt-10">
    <h2 className="font-display text-2xl font-bold uppercase">{title}</h2>
    <ul className="mt-4 space-y-3">{(items ?? []).map((x) => <li key={x} className="flex gap-3 text-slate-300"><span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#C8102E]" />{x}</li>)}</ul>
  </div>
);

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(undefined);
  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(null));
  }, [id]);
  const isSample = /^\[sample\]/i.test(job?.title ?? "");
  useSeo({
    title: job ? `${job.title} – Brownsville, TX | AFR Careers` : "Role | AFR Careers",
    description: job?.summary ?? "America First Refining careers.",
    path: `/careers/${id}`,
    noindex: isSample,
    jsonLd: job && !isSample ? {
      "@context": "https://schema.org", "@type": "JobPosting", title: job.title, description: job.summary, datePosted: job.created_at?.slice(0, 10),
      ...(job.valid_through ? { validThrough: job.valid_through } : {}), employmentType: job.employment_type,
      hiringOrganization: { "@type": "Organization", name: "America First Refining", sameAs: "https://americafirstrefining.com" },
      jobLocation: { "@type": "Place", address: { "@type": "PostalAddress", addressLocality: "Brownsville", addressRegion: "TX", addressCountry: "US" } },
    } : null,
  });

  return (
    <div className="section-light"><div data-testid="job-detail-page" className="mx-auto max-w-[1400px] px-5 pb-24 pt-[170px] md:px-10 md:pt-[196px]">
      <Link to="/careers#openings" data-testid="job-back-link" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" />All roles</Link>
      {job === undefined && <p className="mt-10 text-slate-300">Loading…</p>}
      {job === null && (
        <div data-testid="job-closed" className="mt-10 max-w-xl">
          <h1 className="font-display text-5xl font-black uppercase">This role is closed</h1>
          <p className="mt-4 text-slate-300">It has been filled or removed. See current openings or send a general inquiry.</p>
        </div>
      )}
      {job && (
        <div className="mt-10 grid gap-12 lg:grid-cols-12">
          <article className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4A24C]">{job.department} · {job.location}</p>
            <h1 data-testid="job-title" className="font-display mt-4 text-5xl font-black uppercase leading-[0.9] md:text-7xl">{job.title}</h1>
            <p className="mt-6 text-lg text-slate-200">{job.summary}</p>
            <dl className="mt-8 grid grid-cols-2 gap-4 border-y border-white/10 py-6 text-sm md:grid-cols-3">
              <div><dt className="font-mono text-[11px] uppercase text-slate-400">Type</dt><dd className="mt-1">{job.employment_type?.replace("_", " ")}</dd></div>
              <div><dt className="font-mono text-[11px] uppercase text-slate-400">Compensation</dt><dd className="mt-1">{job.salary_note}</dd></div>
              <div><dt className="font-mono text-[11px] uppercase text-slate-400">Posting owner</dt><dd data-testid="job-owner" className="mt-1">{job.owner}</dd></div>
            </dl>
            <List title="Responsibilities" items={job.responsibilities} />
            <List title="Requirements" items={job.requirements} />
          </article>
          <aside className="lg:col-span-5"><div className="lg:sticky lg:top-28"><ApplyForm job={job} /></div></aside>
        </div>
      )}
    </div></div>
  );
}
